"use strict";
/**
 * Node I/O Slot — Self-descriptive input/output field on a workflow Step.
 *
 * A slot carries ALL metadata needed to render its editor UI, validate user
 * input, and resolve its value at runtime — without any external registry
 * lookup. This makes Steps portable across marketplace import/export: the
 * snapshot is the single source of truth once the step is materialised.
 *
 * The slot is produced by the step-type descriptor (see
 * `step-types/ai.ts::resolveInputFields`) which seeds it from the shared
 * field-schema registry (build-time), then frozen into the snapshot.
 *
 * See `feedback_step_self_descriptive.md`: every step's slots live inline on
 * the step — no runtime registry lookups.
 *
 * Value resolution priority (see `resolveFieldValue`):
 *   1. Mapped value (bound to an upstream step's output)
 *   2. `value` (user-configured constant typed in the editor)
 *   3. `defaultValue` (schema default from the descriptor)
 *   4. `undefined` (if not required) — or thrown error if required
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.isWorkspaceLocalSlot = isWorkspaceLocalSlot;
exports.configSlotValue = configSlotValue;
const WORKSPACE_LOCAL_WIDGETS = new Set([
    'connection-picker',
    'resource-select',
]);
/**
 * Whether the slot's value names something that exists only in the workspace
 * the step lives in: a connection, a resource behind it, or an entry of a
 * `workspace.*` option source (credential, agent, mailbox). Such a value
 * means nothing in another workspace, so a packaged step carries the field
 * empty and it is chosen again after import.
 */
function isWorkspaceLocalSlot(slot) {
    if (slot.widget === undefined)
        return false;
    if (WORKSPACE_LOCAL_WIDGETS.has(slot.widget))
        return true;
    return (slot.widget === 'dynamic-select' &&
        slot.optionsSource !== undefined &&
        slot.optionsSource.startsWith('workspace.'));
}
/**
 * The value a config slot holds for a configuration value: text slots and
 * template editors hold an object as JSON text, every other slot the value.
 */
function configSlotValue(slot, value) {
    if ((slot.type === 'text' || slot.widget === 'template-editor') &&
        value !== null &&
        typeof value === 'object') {
        return JSON.stringify(value, null, 2);
    }
    return value;
}
